import { Flex, Row, Space, Typography } from 'antd';
import React from 'react';
import styled from 'styled-components';
import type {
  ReconDetailsCardRecord,
  RecordType
} from '@melio/payments-recon-api-client-ts';
import { TransactionsourceEnumType } from '@tools/ui/types/generated/graphql';
import { theme } from '@tools/ui-utils/theme.util';
import { t } from '@tools/ui-utils/translate.util';
import {
  AmountWithCurrency,
  DateSnippet
} from '../../../../components/snippets';
import TagStatus from '../../../../components/snippets/tagStatus';
import { reconEnumValue } from '../../../../utils/reconEnumValue';
import { UnmatchRecord } from '../actions/unmatch-record';
import FundsDirection from '../fundsDirection';
import LinkToTransferSearch from '../link-to-transfer-search';
import RelatedBankRecordCell from '../related-bank-record-cell';

const Label = styled(Typography.Text)`
  display: block;
  margin-bottom: 4px;
  color: ${theme.colors.verificationTask.isRevived};
`;

export const CardRecordDetails: React.FC<{
  cardRecord: ReconDetailsCardRecord;
  refetch: () => void;
}> = ({ cardRecord, refetch }) => {
  return (
    <Space direction="vertical" size={20}>
      <div>
        <Label>Reco ID</Label>
        {cardRecord.recordId}
      </div>

      <div>
        <Label>Reconciliation Status</Label>
        {cardRecord.reconciliationStatus}
      </div>

      <div>
        <Label>{t('reco.tableColumns.externalId')}</Label>
        {cardRecord.externalId}
      </div>

      {!!cardRecord.exceptionId && (
        <div>
          <Label>{t('reco.tableColumns.exceptionId')}</Label>
          {cardRecord.exceptionId}
        </div>
      )}

      <div>
        <Label>{t('reco.tableColumns.type')}</Label>
        {cardRecord.transactionType}
      </div>

      <div>
        <Label>{t('reco.tableColumns.settlementDate')}</Label>
        {cardRecord.settlementDate ? (
          <DateSnippet
            date={cardRecord.settlementDate.toString()}
            format="MMM DD, YYYY"
          />
        ) : null}
      </div>

      <div>
        <Label>{t('reco.amount')}</Label>
        <Row>
          {!!cardRecord.direction && (
            <>
              <FundsDirection direction={cardRecord.direction} />
              &nbsp;&nbsp;
            </>
          )}

          <AmountWithCurrency amount={cardRecord.amount} />
        </Row>
      </div>

      <div>
        <Label>{t('reco.tableColumns.bank')}</Label>
        <TagStatus status={cardRecord.bank as TransactionsourceEnumType} />
      </div>

      <div>
        <Label>{t('reco.tableColumns.mid')}</Label>
        {cardRecord.mid}
      </div>

      {!!cardRecord.cardHolderFullName && (
        <div>
          <Label>{t('reco.tableColumns.cardHolder')}</Label>
          {cardRecord.cardHolderFullName}
        </div>
      )}

      <div>
        <Label>{t('reco.tableColumns.network')}</Label>
        {cardRecord.network}
      </div>

      <div>
        <Label>{t('reco.tableColumns.cardProcessor')}</Label>
        {cardRecord.cardProcessor}
      </div>

      <div>
        <Label>{t('reco.tableColumns.status')}</Label>
        {cardRecord.status}
      </div>

      {!!cardRecord?.bankRecordId && (
        <div>
          <Label>{t('reco.tableColumns.settlementRecord')}</Label>
          <RelatedBankRecordCell bankRecordId={cardRecord.bankRecordId} />
        </div>
      )}

      {!!cardRecord.paymentId && (
        <div>
          <Label>Matched Payment</Label>

          <Flex align="center" gap={12}>
            <LinkToTransferSearch
              filters={{ id: `PID ${cardRecord.paymentId}` }}
            >
              {cardRecord.paymentId}
            </LinkToTransferSearch>
            <UnmatchRecord
              recordId={cardRecord.recordId!}
              recordType={reconEnumValue<RecordType>('card')}
              onComplete={refetch}
            />
          </Flex>
        </div>
      )}

      {!!cardRecord?.manualChangesReason && (
        <div>
          <Label>{t('reco.tableColumns.reason')}</Label>
          {cardRecord.manualChangesReason}
        </div>
      )}

      {!!cardRecord?.comment && (
        <div>
          <Label>{t('reco.tableColumns.comment')}</Label>
          {cardRecord.comment}
        </div>
      )}
    </Space>
  );
};
